import * as React from "react";
import me from "models/me";
import { ISavedView, SavedView } from "./saved-view";
import { List } from "immutable";
import { styles } from "./styles";
import { Accordion } from "./accordion";
import { formatMessage } from "lib/globalize-util";
import { DATATEST } from "constants/integration";

type MerchantItems = List<ISavedView> | ISavedView[];
interface IProps {
    currentViewId: string;
    schemaIdWithRemovedFields?: string;
    onLoad: (viewName: string, viewId: string, viewIndex: number) => void;
    onMerchantViewUpdate: (viewId: string, viewName: string, viewIndex: number) => void;
    onMerchantLocalViewUpdate: (viewId: string, viewName: string, viewIndex: number) => void;
    onMerchantViewDelete: (viewId: string, viewName: string, viewIndex: number) => void;
    onUpdate: (viewId: string, viewName: string, viewIndex: number) => void;
    onPromote: (viewId: string, viewName: string, viewIndex: number) => void;
    onReset: (viewId: string, viewName: string, viewIndex: number) => void;
    onDelete: (viewId: string, viewIndex: number, savedView: ISavedView) => void;
    onNewView: () => void;
    onOpenRemovedFieldModal: (event) => void;
    editable: boolean;
    items: List<ISavedView>;
    merchantItems?: MerchantItems;
}

const useViewListState = (items: List<ISavedView>, merchantItems: MerchantItems = []) =>
    React.useMemo(() => {
        const [defaultSavedView, ...savedViews] = items.toArray();
        const merchantViewList = Array.isArray(merchantItems) ? merchantItems : merchantItems?.toArray();
        const shouldRenderSavedViewsList = Boolean(savedViews.length) || Boolean(merchantViewList.length);
        return {
            defaultSavedView,
            shouldRenderSavedViewsList,
            savedViews,
            merchantViewList,
        };
    }, [items, merchantItems]);

export const SavedViews: React.FunctionComponent<IProps> = ({
    currentViewId,
    schemaIdWithRemovedFields,
    onLoad,
    onOpenRemovedFieldModal,
    onUpdate,
    onMerchantViewUpdate,
    onMerchantLocalViewUpdate,
    onMerchantViewDelete,
    onPromote,
    onDelete,
    onNewView,
    onReset,
    editable,
    items,
    merchantItems,
}) => {
    const { defaultSavedView, shouldRenderSavedViewsList, savedViews, merchantViewList } = useViewListState(
        items,
        merchantItems
    );

    const isNuOrderUser = me?.isNuOrderUser();
    return (
        <div role="list">
            {defaultSavedView && (
                <SavedView
                    key={defaultSavedView.id}
                    view={defaultSavedView}
                    onLoad={onLoad}
                    currentViewId={currentViewId}
                    schemaIdWithRemovedFields={schemaIdWithRemovedFields}
                    onOpenRemovedFieldModal={onOpenRemovedFieldModal}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    onNewView={onNewView}
                    onReset={onReset}
                    viewIndex={0}
                    editable={editable}
                    isActive={currentViewId === defaultSavedView.id}
                    isMerchantSavedView={false}
                />
            )}
            {shouldRenderSavedViewsList && (
                <div {...styles}>
                    {Boolean(merchantViewList.length) && (
                        <Accordion
                            title={formatMessage("Merchant Saved Views")}
                            dataTestId={DATATEST.ASSORTMENTS.ROLLUP_ACCORDION_MENU}
                        >
                            <div className="view-group">
                                {merchantViewList.map((item, idx) => (
                                    <SavedView
                                        key={item.id}
                                        view={item}
                                        onLoad={onLoad}
                                        currentViewId={currentViewId}
                                        schemaIdWithRemovedFields={schemaIdWithRemovedFields}
                                        onOpenRemovedFieldModal={onOpenRemovedFieldModal}
                                        onMerchantViewUpdate={
                                            isNuOrderUser ? onMerchantViewUpdate : onMerchantLocalViewUpdate
                                        }
                                        onMerchantViewDelete={isNuOrderUser && onMerchantViewDelete}
                                        onDelete={onDelete}
                                        onNewView={onNewView}
                                        onReset={onReset}
                                        viewIndex={idx}
                                        editable={editable}
                                        isActive={currentViewId === item.id}
                                        isMerchantSavedView={true}
                                    />
                                ))}
                            </div>
                        </Accordion>
                    )}
                    {Boolean(savedViews.length) && (
                        <div>
                            {savedViews.map((item, idx) => (
                                <SavedView
                                    key={item.id}
                                    view={item}
                                    onLoad={onLoad}
                                    currentViewId={currentViewId}
                                    schemaIdWithRemovedFields={schemaIdWithRemovedFields}
                                    onOpenRemovedFieldModal={onOpenRemovedFieldModal}
                                    onPromote={isNuOrderUser && onPromote}
                                    onUpdate={onUpdate}
                                    onDelete={onDelete}
                                    onNewView={onNewView}
                                    onReset={onReset}
                                    viewIndex={idx + 1} // this index has to be the index when thes list included the default saved view, thats why add one
                                    editable={editable}
                                    isActive={currentViewId === item.id}
                                    isMerchantSavedView={false}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
